<template>
    <DsModal
        class="unlayer-email-wizard-modal"
        is-open
        size="fullscreen"
        sticky-desktop-header
        :show-back="currentStageIndex > $options.STAGE_INDEXES.TEMPLATE"
        :show-close="currentStageIndex === $options.STAGE_INDEXES.TEMPLATE"
        :intercept-close="currentStageIndex > $options.STAGE_INDEXES.TEMPLATE"
        :title="title"
        @back="handleModalBack"
        @close="$emit('close')"
    >
        <template #title>
            <div class="header-left">
                <DsModalHeaderTitle class="header-title" :title="title" />

                <slot name="title" />
            </div>
        </template>

        <template #header>
            <slot name="header" />
        </template>

        <template #default>
            <div class="stage-indicator-container">
                <DsGuide
                    class="template-change-tip-popover"
                    data-qa="unlayer-email-broadcast-template-select-guide"
                    position="bottom"
                    :body-text="$t('email.broadcast.unlayerWizard.templateSelectGuideBody')"
                    :confirm-text="$t('global.okgotit')"
                    :show="isTemplateChangeTipVisible"
                    @confirm="handleTemplateChangeTipClosed"
                />
                <StageIndicator
                    :active-stage="currentStageIndex"
                    :stages="stages"
                    @go-to-stage="goToStage"
                />
            </div>

            <div class="stages-container">
                <div v-if="isLoading" class="spinner-container">
                    <DsSpinner data-qa="loading-spinner" />
                </div>

                <slot v-else-if="currentStageIndex === $options.STAGE_INDEXES.TEMPLATE" name="select" />
                <slot v-else name="edit" />
            </div>
        </template>
    </DsModal>
</template>

<script>
import StageIndicator from '@/components/broadcast/StageIndicator';
import { STAGE_STATUS } from '@/components/broadcast/constants/emailBroadcast.constants';
import { TUTORIAL_TYPES } from '@/components/broadcast/constants/tutorials.constants';
import {
    UNLAYER_EMAIL_BROADCAST_WIZARD_STAGE_INDEXES as STAGE_INDEXES,
    UNLAYER_BUILDER_CUSTOMIZATION_HELP_URL as HELP_URL,
} from '@/components/unlayer/constants/unlayer.constants';
import { updateFtuItem, loadFtu } from '@/ftu.utils';

const { TEMPLATE_CHANGE_GUIDE_DISPLAY_DELAY_MS, TEMPLATE_CHANGE_GUIDE } = TUTORIAL_TYPES.UNLAYER_BROADCAST;

export default {
    STAGE_INDEXES,
    HELP_URL,

    name: 'UnlayerEmailWizardContainer',

    components: {
        StageIndicator,
    },

    props: {
        title: String,
        currentStageIndex: {
            type: Number,
            default: STAGE_INDEXES.TEMPLATE,
        },
        isLoading: Boolean,
        appId: String,
        userId: [Number, String],
    },

    data() {
        return {
            isTemplateChangeTipVisible: false,
            ftuItems: {},
            initialStageIndex: STAGE_INDEXES.TEMPLATE,
        };
    },

    created() {
        this.loadFtuItems();
    },

    watch: {
        currentStageIndex() {
            this.templateChangeGuideRoutine();
        },

        ftuItemsLoaded(newVal) {
            if (newVal) {
                this.templateChangeGuideRoutine();
            }
        },
    },

    computed: {
        stages() {
            const { IN_PROGRESS, COMPLETE } = STAGE_STATUS;

            return [
                this.$t('email.broadcast.unlayerWizard.templateStep'),
                this.$t('email.broadcast.unlayerWizard.createStep'),
            ].map((label, index) => {
                const isComplete = index < this.currentStageIndex;

                return {
                    label,
                    status: isComplete ? COMPLETE : IN_PROGRESS,
                    disabled: !isComplete,
                };
            });
        },
    },

    methods: {
        handleModalBack() {
            if (this.currentStageIndex === STAGE_INDEXES.TEMPLATE) {
                this.$emit('close');

                return;
            }

            this.goToStage(this.currentStageIndex - 1);
        },

        goToStage(index) {
            this.$emit('go-to-stage', index);
        },

        async loadFtuItems() {
            this.ftuItems = await loadFtu(this.appId, this.userId);
        },

        templateChangeGuideRoutine() {
            if (this.currentStageIndex !== STAGE_INDEXES.CREATE) {
                this.isTemplateChangeTipVisible = false;

                return;
            }

            if (!this.ftuItemsLoaded) {
                return;
            }

            if (this.ftuItems[TEMPLATE_CHANGE_GUIDE]) {
                return;
            }

            if (this.timeoutHandle) {
                return;
            }

            // Display guide after some time as unlayer template
            // takes some time to load and also because displaying
            // guide immediately does not look good in this scenario
            this.timeoutHandle = setTimeout(() => {
                if (this.currentStageIndex === STAGE_INDEXES.CREATE) {
                    // stage index might have changed by this time, so need to confirm
                    this.isTemplateChangeTipVisible = true;
                }

                this.timeoutHandle = null;
            }, TEMPLATE_CHANGE_GUIDE_DISPLAY_DELAY_MS);
        },

        async handleTemplateChangeTipClosed() {
            this.isTemplateChangeTipVisible = false;
            this.ftuItems[TEMPLATE_CHANGE_GUIDE] = true;

            await updateFtuItem(this.appId, this.userId, TEMPLATE_CHANGE_GUIDE, true);
        },
    }
};
</script>

<style scoped lang="scss">
.header-left {
    display: flex;
}

.header-title {
    display: flex;
    align-items: center;
    margin-right: $spacing-150;
}

.stage-indicator-container {
    position: fixed;
    top: 0;
    height: 1px;
    z-index: 10;
    width: calc(100% - #{$spacing-600});
    text-align: center;
}

.template-change-tip-popover {
    --popover-width: px-to-rem(250);
    left: px-to-rem(125);
    z-index: 2;
}

.stages-container {
    height: calc(100vh - #{px-to-rem(56px)});
    margin: -$spacing-300;
    overflow: hidden;
}

.spinner-container {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
}
</style>

<style lang="scss">
div.unlayer-email-wizard-modal {
    div.modal-header{
        padding: $spacing-100;
    }

    div.modal-body {
        padding: 0;

        ul.tabs {
            margin-bottom: 0;
            margin-top: 0;

            li.tab {
                padding: 0 0.75rem;
            }
        }
    }
}
</style>
